/**
 * Product Card Size Selector (Native Select Version)
 * Handles size selection for complementary products and syncs with main product
 * @module component-card-product-list
 */

(function() {
  'use strict';

  // ===== CONSTANTS =====
  const TIMING = {
    CART_SIZE_SYNC_DELAY: 100,
    RECOMMENDATIONS_LOAD_DELAY: 500,
    SYNC_DEBOUNCE_DELAY: 100,
    INITIAL_SYNC_DELAYS: [0, 500, 1000, 2000],
    MAX_WAIT_FOR_RECOMMENDATIONS: 3000
  };

  // Match standard size formats: S, M, L, XL, 2XL, 3XL, etc.
  // Pattern: Only letters S,M,L,X or numbers, 1-4 characters
  const SIZE_PATTERN = /^[SMLX0-9]+$/;
  const MAX_SIZE_LENGTH = 4;

  // ===== MODULE STATE =====
  let observers = [];
  let sizeListenerSetup = false;
  let syncTimeout = null;
  let pubsubUnsubscribe = null;
  
  // Track products that user manually changed (don't override these)
  const manuallyChangedProducts = new Set();

  // ===== INITIALIZATION =====
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /**
   * Initialize the size selector functionality
   */
  function init() {
    // Listen for size select changes
    document.addEventListener('change', handleSizeSelectChange, true);
    
    setupMainProductSizeListener();
    setupPubSubListener();
    setupRecommendationsObserver();
    setupCartDrawerObserver();
    performInitialSyncs();
    
    // Cleanup on page unload
    window.addEventListener('beforeunload', cleanup);
  }

  /**
   * Cleanup all observers and subscriptions
   */
  function cleanup() {
    observers.forEach(observer => observer.disconnect());
    observers = [];
    
    if (pubsubUnsubscribe) {
      pubsubUnsubscribe();
    }
    
    if (syncTimeout) {
      clearTimeout(syncTimeout);
    }
  }

  /**
   * Handles change events for size select elements
   * @param {Event} e - The change event
   */
  function handleSizeSelectChange(e) {
    const sizeSelect = e.target;
    
    // Only handle our size select elements
    if (!sizeSelect.classList.contains('product-card-size-select')) return;
    
    const sizeSelector = sizeSelect.closest('.product-card-size-selector');
    if (!sizeSelector) return;
    
    const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
    if (!selectedOption) return;
    
    const value = selectedOption.value;
    const variantId = selectedOption.dataset.variantId;
    
    if (!value || !variantId) return;
    
    // Update the form
    const productCard = sizeSelector.closest('.product-card');
    if (productCard) {
      const productId = sizeSelector.dataset.productId;
      
      // Mark this product as manually changed (don't override user's choice)
      if (productId) {
        manuallyChangedProducts.add(productId);
      }
      
      const variantInput = productCard.querySelector(`.product-variant-id[data-product-id="${productId}"]`);
      
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('input', { bubbles: true }));
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Enable the button
      const atcButton = productCard.querySelector('.product-card__atc');
      if (atcButton) {
        atcButton.disabled = false;
        atcButton.removeAttribute('aria-disabled');
      }
    }
  }

  /**
   * Sets up PubSub listener for variant changes
   */
  function setupPubSubListener() {
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      pubsubUnsubscribe = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        handleMainProductVariantChange
      );
    }
  }

  /**
   * Sets up observer for recommendations loading
   * Uses MutationObserver instead of multiple setTimeout calls
   */
  function setupRecommendationsObserver() {
    const targetNode = document.body;
    
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const hasRecommendations = document.querySelector('.complementary-products, .cart-drawer-product-card');
          if (hasRecommendations) {
            syncWithCurrentContext();
            // Don't disconnect - recommendations can be added dynamically
          }
        }
      }
    });
    
    observer.observe(targetNode, {
      childList: true,
      subtree: true
    });
    
    observers.push(observer);
    
    // Fallback timeout
    setTimeout(() => {
      syncWithCurrentContext();
    }, TIMING.MAX_WAIT_FOR_RECOMMENDATIONS);
  }

  /**
   * Syncs with current context (cart drawer or product page)
   */
  function syncWithCurrentContext() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && (cartDrawer.hasAttribute('open') || cartDrawer.classList.contains('active'))) {
      const cartSize = getSizeFromCartItems();
      if (cartSize) {
        debouncedSync(cartSize);
      }
    } else {
      const currentSize = getCurrentMainProductSize();
      if (currentSize) {
        debouncedSync(currentSize);
      }
    }
  }

  /**
   * Sets up cart drawer observer
   */
  function setupCartDrawerObserver() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    
    let wasOpen = false;
    
    const cartObserver = new MutationObserver(() => {
      const isOpen = cartDrawer.hasAttribute('open') || cartDrawer.classList.contains('active');
      
      if (isOpen && !wasOpen) {
        // Cart drawer just opened
        // Reset manual changes for cart drawer products only (allow fresh sync)
        resetCartDrawerManualChanges();
        
        setTimeout(() => {
          const cartSize = getSizeFromCartItems();
          if (cartSize) {
            debouncedSync(cartSize);
          }
        }, TIMING.RECOMMENDATIONS_LOAD_DELAY);
      }
      
      wasOpen = isOpen;
    });
    
    cartObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['open', 'class']
    });
    
    observers.push(cartObserver);
  }
  
  /**
   * Resets manual changes tracking for cart drawer products only
   * This allows fresh syncing when cart drawer reopens
   */
  function resetCartDrawerManualChanges() {
    const cartDrawerCards = document.querySelectorAll('.cart-drawer-product-card');
    
    cartDrawerCards.forEach(card => {
      const sizeSelector = card.querySelector('.product-card-size-selector');
      if (sizeSelector) {
        const productId = sizeSelector.dataset.productId;
        if (productId) {
          manuallyChangedProducts.delete(productId);
        }
      }
    });
  }

  /**
   * Performs initial syncs with delays
   */
  function performInitialSyncs() {
    TIMING.INITIAL_SYNC_DELAYS.forEach(delay => {
      if (delay === 0) {
        syncWithCurrentContext();
      } else {
        setTimeout(() => {
          syncWithCurrentContext();
        }, delay);
      }
    });
  }

  /**
   * Listens for recommendations:loaded event
   */
  document.addEventListener('recommendations:loaded', () => {
    syncWithCurrentContext();
  });

  /**
   * Retrieves the current size selection from the main product form
   * @returns {string|null} The selected size or null if not found
   */
  function getCurrentMainProductSize() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return null;
    
    // Find the size input field
    const sizeInput = Array.from(variantSelects.querySelectorAll('.product-form__input')).find(input => {
      const label = input.querySelector('label, legend');
      return label && label.textContent.toLowerCase().includes('size');
    });
    
    if (sizeInput) {
      const sizeSelect = sizeInput.querySelector('select');
      const sizeRadio = sizeInput.querySelector('input[type="radio"]:checked');
      
      if (sizeSelect && sizeSelect.value) {
        return sizeSelect.value;
      } else if (sizeRadio && sizeRadio.value) {
        return sizeRadio.value;
      }
    }
    
    return null;
  }

  /**
   * Sets up listener for main product size changes using event delegation
   */
  function setupMainProductSizeListener() {
    if (sizeListenerSetup) return;
    
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;
    
    variantSelects.addEventListener('change', function(e) {
      const target = e.target;
      
      // Check if this is from a complementary product (skip those)
      if (target.closest('.complementary-product-card')) return;
      
      // Check if this is a size input change
      const sizeInput = target.closest('.product-form__input');
      if (!sizeInput) return;
      
      const label = sizeInput.querySelector('label, legend');
      if (!label || !label.textContent.toLowerCase().includes('size')) return;
      
      // Get the selected size value
      let selectedSize = null;
      
      if (target.type === 'radio' && target.checked) {
        selectedSize = target.value;
      } else if (target.tagName === 'SELECT' && target.value) {
        selectedSize = target.value;
      }
      
      if (selectedSize) {
        debouncedSync(selectedSize);
      }
    });
    
    sizeListenerSetup = true;
  }

  /**
   * Handles main product variant change events
   * @param {Object} event - The variantChange event
   */
  function handleMainProductVariantChange(event) {
    const sectionId = event.data?.sectionId;
    const variant = event.data?.variant;
    if (!variant || !sectionId) return;
    
    // Check if this is from a complementary product section (skip those)
    const complementarySection = document.querySelector(`.complementary-products[data-section-id="${sectionId}"]`);
    if (complementarySection) return;
    
    // Find size option from variant-selects
    const variantSelects = document.querySelector('variant-selects');
    let selectedSize = null;
    
    if (variantSelects) {
      selectedSize = extractSizeFromVariantSelects(variantSelects, variant);
    }
    
    if (selectedSize) {
      debouncedSync(selectedSize);
    }
  }

  /**
   * Extracts size from variant-selects element
   * @param {HTMLElement} variantSelects - The variant-selects element
   * @param {Object} variant - The variant object
   * @returns {string|null} The size value or null
   */
  function extractSizeFromVariantSelects(variantSelects, variant) {
    const optionInputs = Array.from(variantSelects.querySelectorAll('.product-form__input'));
    let sizeOptionIndex = -1;
    
    // Find which option is "Size" by checking labels
    optionInputs.forEach((input, index) => {
      const label = input.querySelector('label, legend');
      if (label && label.textContent.toLowerCase().includes('size')) {
        sizeOptionIndex = index;
      }
    });
    
    // Get the selected size value
    if (sizeOptionIndex >= 0) {
      const sizeInput = optionInputs[sizeOptionIndex];
      const sizeSelect = sizeInput.querySelector('select');
      const sizeRadio = sizeInput.querySelector('input[type="radio"]:checked');
      
      if (sizeSelect && sizeSelect.value) {
        return sizeSelect.value;
      } else if (sizeRadio && sizeRadio.value) {
        return sizeRadio.value;
      } else {
        // Fallback: get from variant options based on position
        if (sizeOptionIndex === 0 && variant.option1) return variant.option1;
        else if (sizeOptionIndex === 1 && variant.option2) return variant.option2;
        else if (sizeOptionIndex === 2 && variant.option3) return variant.option3;
      }
    }
    
    // Fallback: try to detect from variant options
    if (optionInputs.length > 0) {
      if (variant.option1) {
        const firstInput = optionInputs[0];
        const firstLabel = firstInput.querySelector('label, legend');
        if (firstLabel && firstLabel.textContent.toLowerCase().includes('size')) {
          return variant.option1;
        }
      }
      if (variant.option2 && optionInputs.length > 1) {
        const secondInput = optionInputs[1];
        const secondLabel = secondInput.querySelector('label, legend');
        if (secondLabel && secondLabel.textContent.toLowerCase().includes('size')) {
          return variant.option2;
        }
      }
      if (variant.option3 && optionInputs.length > 2) {
        const thirdInput = optionInputs[2];
        const thirdLabel = thirdInput.querySelector('label, legend');
        if (thirdLabel && thirdLabel.textContent.toLowerCase().includes('size')) {
          return variant.option3;
        }
      }
    }
    
    return null;
  }

  /**
   * Debounced sync function to prevent race conditions
   * @param {string} selectedSize - The size value to sync
   */
  function debouncedSync(selectedSize) {
    // Cancel previous pending sync
    if (syncTimeout) {
      clearTimeout(syncTimeout);
    }
    
    // Debounce the sync operation
    syncTimeout = setTimeout(() => {
      performSync(selectedSize);
      syncTimeout = null;
    }, TIMING.SYNC_DEBOUNCE_DELAY);
  }

  /**
   * Performs the actual sync operation
   * @param {string} selectedSize - The size value to sync
   */
  function performSync(selectedSize) {
    const complementaryCards = document.querySelectorAll(
      '.complementary-product-card, .cart-drawer-product-card'
    );
    
    complementaryCards.forEach(card => {
      syncCardSize(card, selectedSize);
    });
  }

  /**
   * Synchronizes size for a single card (using native select)
   * @param {HTMLElement} card - The product card element
   * @param {string} selectedSize - The size value to sync
   */
  function syncCardSize(card, selectedSize) {
    const sizeSelector = card.querySelector('.product-card-size-selector');
    if (!sizeSelector) return;
    
    const productId = sizeSelector.dataset.productId;
    
    // ⭐ RESPECT USER CHOICE: Don't override manually changed products
    if (productId && manuallyChangedProducts.has(productId)) {
      return;
    }
    
    const sizeSelect = sizeSelector.querySelector('.product-card-size-select');
    if (!sizeSelect) return;
    
    // Find the matching option by value (case insensitive)
    const options = Array.from(sizeSelect.options);
    const matchingOption = options.find(option => {
      const value = option.value;
      return value && value.toUpperCase() === selectedSize.toUpperCase();
    });
    
    if (matchingOption) {
      const value = matchingOption.value;
      const variantId = matchingOption.dataset.variantId;
      
      if (value && variantId) {
        // Set the select value
        sizeSelect.value = value;
        
        // Update form
        const variantInput = card.querySelector(`.product-variant-id[data-product-id="${productId}"]`);
        
        if (variantInput) {
          variantInput.value = variantId;
        }
        
        // Enable the button
        const atcButton = card.querySelector('.product-card__atc');
        if (atcButton) {
          atcButton.disabled = false;
          atcButton.removeAttribute('aria-disabled');
        }
      }
    }
  }

  /**
   * Retrieves the current size selection from cart items
   * @returns {string|null} The detected size or null if not found
   */
  function getSizeFromCartItems() {
    const cartItems = document.querySelectorAll('.cart-item[data-variant-id]');
    
    for (const item of cartItems) {
      const size = extractSizeFromOptionPairs(item) || 
                   extractSizeFromCommaSeparated(item);
      if (size) return size;
    }
    
    return null;
  }

  /**
   * Extracts size from cart item option name/value pairs (main cart page structure)
   * @param {HTMLElement} item - Cart item element
   * @returns {string|null} Size value or null
   */
  function extractSizeFromOptionPairs(item) {
    const optionNames = item.querySelectorAll('.cart-item__option-name');
    
    for (const optionName of optionNames) {
      if (optionName.textContent.toLowerCase().includes('size')) {
        const optionValue = optionName.nextElementSibling;
        if (optionValue && optionValue.classList.contains('cart-item__option-value')) {
          return optionValue.textContent.trim();
        }
      }
    }
    
    return null;
  }

  /**
   * Extracts size from comma-separated option values (cart drawer structure)
   * @param {HTMLElement} item - Cart item element
   * @returns {string|null} Size value or null
   */
  function extractSizeFromCommaSeparated(item) {
    const options = item.querySelector('.cart-item__options');
    if (!options) return null;
    
    const optionValues = options.querySelectorAll('.cart-item__option-value');
    
    if (optionValues.length > 0) {
      for (const optionValue of optionValues) {
        const text = optionValue.textContent.trim();
        const values = text.split(',').map(v => v.trim());
        
        for (const value of values) {
          // Check if it looks like a size (S, M, L, XL, 2XL, 3XL, etc)
          if (SIZE_PATTERN.test(value.toUpperCase()) && 
              value.length > 0 && 
              value.length <= MAX_SIZE_LENGTH) {
            return value;
          }
        }
      }
      
      // Fallback: try first value
      const firstValue = optionValues[0].textContent.trim();
      const firstPart = firstValue.split(',')[0].trim();
      if (SIZE_PATTERN.test(firstPart.toUpperCase()) && firstPart.length <= MAX_SIZE_LENGTH) {
        return firstPart;
      }
    }
    
    return null;
  }
})();
